import { useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data));
  }, []);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (testimonials.length === 0) return null;

  return (
    <section id="testimonials" className="py-20 sm:py-28 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <Badge className="bg-green-100 text-green-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium">
            Patient Stories
          </Badge>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-4">
            What Our Patients Say
          </h2>
          <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
            Hear from real patients who trust IV Medics for their healthcare needs.
          </p>
        </div>

        {/* Testimonial Cards - Horizontal Scroll on Mobile, Grid on Desktop */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} index={i} />
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <TestimonialCard testimonial={testimonials[currentIndex]} index={0} />
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={prev}
              className="rounded-full w-10 h-10 p-0 border-stone-200"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex ? "bg-green-700 w-6" : "bg-stone-300"
                  }`}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={next}
              className="rounded-full w-10 h-10 p-0 border-stone-200"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Additional testimonials on desktop */}
        {testimonials.length > 3 && (
          <div className="hidden md:grid md:grid-cols-2 gap-6 mt-6">
            {testimonials.slice(3, 5).map((t, i) => (
              <TestimonialCard key={t.id} testimonial={t} index={i + 3} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial, index }) {
  const colors = [
    "bg-green-50 border-green-100",
    "bg-orange-50 border-orange-100",
    "bg-stone-50 border-stone-100",
    "bg-green-50 border-green-100",
    "bg-orange-50 border-orange-100",
  ];

  return (
    <div
      data-testid={`testimonial-card-${index}`}
      className={`rounded-2xl border p-6 sm:p-8 transition-all hover:shadow-md ${colors[index % 5]}`}
    >
      <Quote className="w-8 h-8 text-green-700/20 mb-4" />
      <p className="text-stone-700 text-sm sm:text-base leading-relaxed mb-6 italic">
        "{testimonial.message}"
      </p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-green-700 flex items-center justify-center text-white font-[Outfit] font-bold text-sm">
          {testimonial.patient_name.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-stone-900 text-sm">{testimonial.patient_name}</p>
          <div className="flex gap-0.5 mt-0.5">
            {Array.from({ length: testimonial.rating }).map((_, i) => (
              <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
